/**
 * @file `LearnerDetail` — drawer view of one learner, with the
 *       operator status-flip control.
 * @module @labcraft/admin/ui/learners/learner-detail
 *
 * Renders identity (avatar + name + email + status pill), a
 * key/value grid of timestamps, and a per-row status select for
 * operator moderation. Status changes go through
 * `useLearnerSetStatusMutation`; on success the hook invalidates the
 * `['admin','learners']` prefix so the panel refetches with the
 * updated row.
 *
 * Phase 9 of umbrella #406: replaced the parent's `onUpdated`
 * callback-passing (which patched the parent's `useState<Learner[]>`
 * snapshot) with mutation-driven invalidation. The parent no longer
 * owns the list — TanStack does.
 */

import { useCallback, useState, type JSX } from 'react'
import { Link } from 'react-router'
import type { Learner, LearnerStatus } from '../../shared/routes.ts'
import {
  useAllTags,
  useAttachTag,
  useDetachTag,
  useLearnerSetStatusMutation,
  useLearnerTags,
  useLearnerUpdateProfileMutation,
} from '../hooks/index.ts'
import { buildProfilePatch } from './profile_patch.ts'
import { StatusPill } from './status_pill.tsx'

/** Props for {@link LearnerDetail}. */
export type LearnerDetailProps = {
  learner: Learner
  onClose: () => void
}

const STATUSES: ReadonlyArray<{ id: LearnerStatus; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'banned', label: 'Banned' },
]

/** Render the detail drawer. */
export function LearnerDetail({
  learner,
  onClose,
}: LearnerDetailProps): JSX.Element {
  const mutation = useLearnerSetStatusMutation()

  const onStatusChange = useCallback(
    (next: LearnerStatus) => {
      if (next === learner.status) return
      // Every transition is a silent status flip. The welcome mail is
      // sent by the account daemon on first signup, not from here.
      mutation.mutate({ id: learner.id, status: next })
    },
    [learner.id, learner.status, mutation],
  )

  const saving = mutation.isPending
  const error = mutation.isError
    ? mutation.error.message || 'Status update failed.'
    : null

  return (
    <div className="learner-detail">
      <header className="detail-head">
        <div className="detail-head__crumb">
          Learners &nbsp;›&nbsp; <b>{learner.email}</b>
        </div>
        <button
          type="button"
          className="detail-head__close"
          aria-label="Close detail"
          onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="detail-id">
        <span className="detail-id__avatar">{initials(learner)}</span>
        <div className="detail-id__main">
          <h2
            className={
              learner.displayName
                ? 'detail-id__name'
                : 'detail-id__name detail-id__name--empty'
            }>
            {learner.displayName || '—'}
          </h2>
          <Link
            to={`/emails?recipient=${learner.id}`}
            className="detail-id__email detail-id__email--link"
            title="Compose an email to this learner">
            {learner.email}
          </Link>
        </div>
        <span className="detail-id__pill">
          <StatusPill status={learner.status} />
        </span>
      </div>
      <section className="detail-section">
        <h3 className="detail-section__h">Status</h3>
        <div className="status-control">
          <select
            className="lc-input"
            aria-label="Learner status"
            value={learner.status}
            disabled={saving}
            onChange={e => onStatusChange(e.target.value as LearnerStatus)}>
            {STATUSES.map(s => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          {saving && <span className="status-control__hint">Saving…</span>}
          {error !== null && (
            <span className="status-control__error">{error}</span>
          )}
        </div>
        <p className="status-control__help">
          {`// active: full access. inactive: signed in but blocked from /learn `}
          {`— covers both fresh signups and operator pause. banned: signed out `}
          {`+ blocked. The welcome email is sent automatically on first signup, `}
          {`not on status changes here.`}
        </p>
      </section>
      <section className="detail-section">
        <h3 className="detail-section__h">Tags</h3>
        <TagEditor learnerId={learner.id} />
        <p className="status-control__help">
          {`// operator-assigned groups. used to target campaigns and filter `}
          {`the roster. tags are lower-cased on save.`}
        </p>
      </section>
      <section className="detail-section">
        <h3 className="detail-section__h">Profile</h3>
        <ProfileEditor key={learner.id} learner={learner} />
      </section>
      <section className="detail-section">
        <h3 className="detail-section__h">Reference</h3>
        <dl className="kv-grid">
          <dt>id</dt>
          <dd>{learner.id}</dd>
        </dl>
      </section>
      <section className="detail-section">
        <h3 className="detail-section__h">Lifecycle</h3>
        <dl className="kv-grid">
          <dt>signed up</dt>
          <dd>{formatLong(learner.createdAt)}</dd>
          <dt>profile filled</dt>
          <dd className={learner.profileCompletedAt ? '' : 'empty'}>
            {learner.profileCompletedAt
              ? formatLong(learner.profileCompletedAt)
              : '—'}
          </dd>
        </dl>
      </section>
    </div>
  )
}

/** Props for {@link ProfileEditor}. */
type ProfileEditorProps = {
  learner: Learner
}

/**
 * The operator profile-edit form inside the detail drawer (#1639). Makes
 * `display_name` / `timezone` / `avatar_url` editable; `email` renders
 * read-only. Save is enabled only when the draft differs from the current
 * row ({@link buildProfilePatch} derives the minimal patch); on success the
 * mutation invalidates the learners prefix so the row + header re-render.
 * The parent keys this by `learner.id` so switching rows resets the draft.
 */
function ProfileEditor({ learner }: ProfileEditorProps): JSX.Element {
  const mutation = useLearnerUpdateProfileMutation()
  const [displayName, setDisplayName] = useState(learner.displayName)
  const [timezone, setTimezone] = useState(learner.timezone ?? '')
  const [avatarUrl, setAvatarUrl] = useState(learner.avatarUrl ?? '')

  const patch = buildProfilePatch(learner, { displayName, timezone, avatarUrl })
  const dirty = Object.keys(patch).length > 0
  const saving = mutation.isPending
  const error = mutation.isError
    ? mutation.error.message || 'Profile update failed.'
    : null
  const saved = mutation.isSuccess && !dirty

  const onSave = useCallback(() => {
    if (Object.keys(patch).length === 0) return
    mutation.mutate({ id: learner.id, patch })
  }, [patch, mutation, learner.id])

  return (
    <div className="profile-editor">
      <label className="field">
        <span className="field-label">Display name</span>
        <input
          className="lc-input"
          type="text"
          value={displayName}
          disabled={saving}
          onChange={e => setDisplayName(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Timezone</span>
        <input
          className="lc-input"
          type="text"
          placeholder="e.g. America/New_York"
          value={timezone}
          disabled={saving}
          onChange={e => setTimezone(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Avatar URL</span>
        <input
          className="lc-input"
          type="url"
          placeholder="https://…"
          value={avatarUrl}
          disabled={saving}
          onChange={e => setAvatarUrl(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Email (read-only)</span>
        <input
          className="lc-input"
          type="email"
          value={learner.email}
          readOnly
          disabled
        />
      </label>
      <div className="profile-editor__actions">
        <button
          type="button"
          className="lc-btn lc-btn--primary"
          disabled={!dirty || saving}
          onClick={onSave}>
          {saving ? 'Saving…' : 'Save profile'}
        </button>
        {saved && <span className="status-control__hint">Saved.</span>}
        {error !== null && (
          <span className="status-control__error">{error}</span>
        )}
      </div>
    </div>
  )
}

/** Props for {@link TagEditor}. */
type TagEditorProps = {
  learnerId: string
}

/**
 * The learner-tags editor inside the detail drawer (#1639). Renders the
 * learner's current tags as removable chips (seeded from the list's
 * `tagsByLearnerId`, refreshed via `useLearnerTags`) plus an add control
 * with a datalist autocomplete over every existing tag. Every mutation
 * invalidates the learners prefix so the list chips + autocomplete stay in
 * sync. Tags are canonicalized (lower-cased) by the account daemon on save.
 */
function TagEditor({ learnerId }: TagEditorProps): JSX.Element {
  const tagsQuery = useLearnerTags(learnerId)
  const allTags = useAllTags()
  const attach = useAttachTag()
  const detach = useDetachTag()
  const [draft, setDraft] = useState('')

  const tags = tagsQuery.data ?? []
  const busy = attach.isPending || detach.isPending
  const suggestions = (allTags.data ?? []).filter(t => !tags.includes(t))
  const error =
    attach.isError || detach.isError
      ? (attach.error ?? detach.error)?.message || 'Tag update failed.'
      : null

  const onAdd = useCallback(() => {
    const value = draft.trim()
    if (value === '') return
    attach.mutate({ id: learnerId, tag: value })
    setDraft('')
  }, [draft, attach, learnerId])

  return (
    <div className="tag-editor">
      <div className="tag-chips">
        {tags.length === 0 && (
          <span className="tag-chips__empty">// no tags</span>
        )}
        {tags.map(t => (
          <span key={t} className="tag-chip">
            <span className="tag-chip__label">{t}</span>
            <button
              type="button"
              className="tag-chip__x"
              aria-label={`Remove tag ${t}`}
              disabled={busy}
              onClick={() => detach.mutate({ id: learnerId, tag: t })}>
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="tag-add">
        <input
          className="lc-input"
          type="text"
          list="lc-all-tags"
          placeholder="Add a tag…"
          aria-label="Add a tag"
          value={draft}
          disabled={busy}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
        />
        <datalist id="lc-all-tags">
          {suggestions.map(t => (
            <option key={t} value={t} />
          ))}
        </datalist>
        <button
          type="button"
          className="lc-btn"
          disabled={busy || draft.trim() === ''}
          onClick={onAdd}>
          Add
        </button>
      </div>
      {error !== null && <span className="status-control__error">{error}</span>}
    </div>
  )
}

/** Compute a two-character avatar label from a learner. */
function initials(l: Learner): string {
  const name = l.displayName.trim()
  if (name.length > 0) {
    const parts = name.split(/\s+/)
    const a = parts[0]?.[0] ?? ''
    const b = parts[1]?.[0] ?? ''
    const out = (a + b).toUpperCase()
    if (out.length > 0) return out
  }
  const local = l.email.split('@')[0] ?? l.email
  return local.slice(0, 2).toUpperCase()
}

/** `2026-05-21T12:00:00Z` → `May 21, 2026`. */
function formatLong(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
